import React, { useState, useEffect } from 'react';
import axios from "axios";
import AdminHeaderNew from '../NewAdminPanel/Common/AdminHeader';
import AdminSideBarNew from '../NewAdminPanel/Common/AdminSidebarMnt';
import AdminFooterNew from '../NewAdminPanel/Common/AdminFooter';

const CRMCHCUpdatedData = () => {
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [rows, setRows] = useState([]);

    // table utilities: sorting and pagination
    const [sortKey, setSortKey] = useState('');
    const [sortDir, setSortDir] = useState('asc');
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    // filter state — were missing, caused runtime crash
    const [selectedState, setSelectedState] = useState('');      // will hold stateCode as string
    const [selectedDistrict, setSelectedDistrict] = useState('');
    const [selectedSubDistrict, setSelectedSubDistrict] = useState('');
    const [selectedFinYear, setSelectedFinYear] = useState('');

    // master state list from API so that all states are visible in dropdown
    const [statesData, setStatesData] = useState([]);
    const [districtsData, setDistrictsData] = useState([]);
    const [subDistrictsData, setSubDistrictsData] = useState([]);

    // Login level: Central=1, State=2, District=3
    const userRole = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem("userRoleCodeModel") : null;
    const userType = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem("userTypeCodeModel") : null;
    const isCentralLogin = String(userRole) === "1" || String(userType) === "1";
    const isStateFrozen = String(userRole) === "2" || String(userType) === "2";
    const isDistrictFrozen = String(userRole) === "3" || String(userType) === "3";

    // master financial year list (all years seen so far, independent of current filter)
    const [allFinYears, setAllFinYears] = useState([]);

    const baseUrl = `${process.env.NODE_ENV === 'development' ? process.env.REACT_APP_DEV_MODE : process.env.REACT_APP_PRO_MODE}`;

    const columns = [
        { key: 'stateName', label: 'State Name' },
        { key: 'finYear', label: 'Fin Year' },
        { key: 'total_Implements', label: 'Total Implements' },
        { key: 'working_Count', label: 'Working Count' },
        { key: 'notWorking_Count', label: 'Not Working Count' },
    ];

    // initial fetch of master state list (for State dropdown)
    useEffect(() => {
        const fetchStates = async () => {
            try {
                const res = await axios.get(
                    `${baseUrl}/api/states`,
                    
                    { headers: { accept: '*/*' } }
                );
                setStatesData(res?.data?.dataContent ?? []);
            } catch (e) {
                console.error('Error fetching states list:', e);
            }
        };
        fetchStates();
    }, [baseUrl]);

    // Freeze state/district for State/District login on mount
    useEffect(() => {
        const ur = sessionStorage.getItem("userRoleCodeModel");
        const ut = sessionStorage.getItem("userTypeCodeModel");
        const stateCodeSess = sessionStorage.getItem("stateCodeModel") || sessionStorage.getItem("stateNameModel");
        const districtCodeSess = sessionStorage.getItem("districtCodeModel") || sessionStorage.getItem("districtNameModel");
        const subDistrictCodeSess = sessionStorage.getItem("subDistrictCodeModel") || sessionStorage.getItem("subDistrictNameModel");
        if ((String(ur) === "2" || String(ut) === "2") && stateCodeSess) {
            setSelectedState(String(stateCodeSess).trim());
        }
        if ((String(ur) === "3" || String(ut) === "3") && stateCodeSess) {
            setSelectedState(String(stateCodeSess).trim());
            if (districtCodeSess) {
                const dc = String(districtCodeSess).includes(',')
                    ? String(districtCodeSess).split(',')[0].trim()
                    : String(districtCodeSess);
                setSelectedDistrict(dc);
            }
            if (subDistrictCodeSess) setSelectedSubDistrict(String(subDistrictCodeSess));
        }
    }, []);

    // Re-bind state when statesData loads (ensures dropdown value matches option format)
    useEffect(() => {
        if (statesData.length === 0) return;
        const ur = sessionStorage.getItem("userRoleCodeModel");
        const ut = sessionStorage.getItem("userTypeCodeModel");
        const stateCodeSess = sessionStorage.getItem("stateCodeModel") || sessionStorage.getItem("stateNameModel");
        if ((String(ur) !== "2" && String(ut) !== "2") && (String(ur) !== "3" && String(ut) !== "3")) return;
        if (!stateCodeSess) return;
        const sessVal = String(stateCodeSess).trim();
        // Resolve: if sessVal is numeric, use as-is; else find state by name
        const isNumeric = /^\d+$/.test(sessVal);
        const match = isNumeric
            ? statesData.find(s => String(s.stateCode) === sessVal)
            : statesData.find(s => (s.stateName || '').toLowerCase() === sessVal.toLowerCase());
        if (match) {
            setSelectedState(String(match.stateCode));
        } else if (isNumeric) {
            setSelectedState(sessVal);
        }
    }, [statesData]);

    // Fetch districts when state is selected (skip for Central - no District dropdown)
    useEffect(() => {
        if (isCentralLogin || !selectedState) {
            setDistrictsData([]);
            if (!isDistrictFrozen) setSelectedDistrict('');
            return;
        }
        const districtCodeSess = sessionStorage.getItem("districtCodeModel");
        const fetchDistricts = async () => {
            try {
                const res = await axios.post(
                    `${baseUrl}/api/district`,
                    { stateCode: selectedState },
                    { headers: { 'Content-Type': 'application/json', accept: '*/*' } }
                );
                const content = res?.data?.dataContent ?? [];
                let list = Array.isArray(content) ? content : [];
                if (isDistrictFrozen && districtCodeSess) {
                    const codes = String(districtCodeSess).split(',').map(c => c.trim());
                    list = list.filter(d => codes.includes(String(d.districtCode)));
                }
                setDistrictsData(list);
            } catch (e) {
                console.error('Error fetching districts:', e);
                setDistrictsData([]);
            }
        };
        fetchDistricts();
    }, [selectedState, isDistrictFrozen, isCentralLogin]);

    // Fetch subdistricts when district is selected (for District login)
    useEffect(() => {
        if (!selectedDistrict || !selectedState) {
            setSubDistrictsData([]);
            if (!isDistrictFrozen) setSelectedSubDistrict('');
            return;
        }
        const fetchSubDistricts = async () => {
            try {
                const res = await axios.post(
                    `${baseUrl}/api/subdistrict`,
                    {
                        stateCode: selectedState,
                        districtCode: selectedDistrict
                    },
                    { headers: { 'Content-Type': 'application/json', accept: '*/*' } }
                );
                const content = res?.data?.dataContent ?? [];
                setSubDistrictsData(Array.isArray(content) ? content : []);
            } catch (e) {
                console.error('Error fetching subdistricts:', e);
                setSubDistrictsData([]);
            }
        };
        fetchSubDistricts();
    }, [selectedState, selectedDistrict, isDistrictFrozen]);

    useEffect(() => {
        fetchData();
    }, []);
    //alert(sessionStorage.getItem("userRoleCodeModel"));

    const fetchData = async () => {
        setLoading(true);
        setRows([]);
        setErrorMessage('');
        try {
            const requestUrl = `${baseUrl}/api/MT/CRMCHCUpdatedData`;

            const userType = sessionStorage.getItem("userRoleCodeModel");
            const stateCodeSess = sessionStorage.getItem("stateNameModel");
            const districtCodeSess = sessionStorage.getItem("districtCodeModel");

            // Build payload — always fetch ALL data; filtering is done client-side
            const payload = {
                stateCode: 0,
                districtCode: 0,
                flag: "",
                finYear: ""
            };

            if (userType === "2") {
                payload.stateCode = Number(stateCodeSess);
                payload.flag = "State";
            }

            if (userType === "3") {
                payload.stateCode = Number(stateCodeSess) || 0;
                payload.districtCode = Number(districtCodeSess) || 0;
                payload.flag = "District";
            }

            const res = await axios.post(
                requestUrl,
                payload, // ✅ actual body
                {
                    headers: {
                        'Content-Type': 'application/json',
                        'Accept': '*/*'
                    }
                }
            );

            const content = res?.data?.dataContent ?? [];
            const safeContent = Array.isArray(content) ? content : [];
            setRows(safeContent);

            // Build fin year list from full response
            const yearsFromResponse = Array.from(
                new Set(
                    safeContent
                        .filter(r => r.stateName !== 'Total')
                        .map(r => r.finYear)
                        .filter(Boolean)
                )
            );
            if (yearsFromResponse.length > 0) {
                setAllFinYears(Array.from(new Set(yearsFromResponse)).sort());
            }
        } catch (e) {
            // If the server responded with non-2xx but still sent dataContent, use it
            if (e.response?.data?.dataContent) {
                const content = e.response.data.dataContent;
                const safeContent = Array.isArray(content) ? content : [];
                setRows(safeContent);
                const yearsFromResponse = Array.from(
                    new Set(safeContent.filter(r => r.stateName !== 'Total').map(r => r.finYear).filter(Boolean))
                );
                if (yearsFromResponse.length > 0) setAllFinYears(Array.from(new Set(yearsFromResponse)).sort());
            } else {
                const detail = e.response
                    ? `HTTP ${e.response.status}: ${JSON.stringify(e.response.data)}`
                    : e.message;
                console.error('Error fetching CRMCHCUpdatedData:', detail, e);
                setErrorMessage(`Failed to fetch data. Please try again.`);
            }
        } finally {
            setLoading(false);
        }
    };

    const getValue = (row, key) => row[key] ?? '';

    const handleSort = (key) => {
        if (sortKey === key) {
            setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
        } else {
            setSortKey(key);
            setSortDir('asc');
        }
        setCurrentPage(1);
    };

    const goToPage = (p) => {
        const totalPages = Math.max(1, Math.ceil(filteredRows().length / pageSize));
        setCurrentPage(Math.min(Math.max(1, p), totalPages));
    };

    // Apply State + District + SubDistrict + FinYear filters, exclude any server-side "Total" row
    const filteredRows = () => {
        const selectedStateName = selectedState
            ? (statesData.find(s => String(s.stateCode) === String(selectedState))?.stateName || '')
            : '';
        const selectedDistrictName = selectedDistrict
            ? (districtsData.find(d => String(d.districtCode) === String(selectedDistrict))?.districtName || '')
            : '';
        const selectedSubDistrictName = selectedSubDistrict
            ? (subDistrictsData.find(s => String(s.subDistrictCode) === String(selectedSubDistrict))?.subDistrictName || '')
            : '';

        return rows
            .filter(r => r.stateName !== 'Total')
            .filter(r => !selectedStateName || r.stateName === selectedStateName)
            .filter(r => !selectedDistrictName || (r.districtName || r.districtNames || '') === selectedDistrictName)
            .filter(r => !selectedSubDistrictName || (r.subdistrictName || r.subDistrictName || r.blockName || '') === selectedSubDistrictName)
            .filter(r => !selectedFinYear || r.finYear === selectedFinYear);
    };

    const sortedRows = () => {
        const data = [...filteredRows()];
        if (!sortKey) return data;
        data.sort((a, b) => {
            const av = String(getValue(a, sortKey)).toLowerCase();
            const bv = String(getValue(b, sortKey)).toLowerCase();
            if (av < bv) return sortDir === 'asc' ? -1 : 1;
            if (av > bv) return sortDir === 'asc' ? 1 : -1;
            return 0;
        });
        return data;
    };

    const getPageRows = () => {
        const sorted = sortedRows();
        const start = (currentPage - 1) * pageSize;
        return sorted.slice(start, start + pageSize);
    };

    // Totals calculated from filtered (not paginated) data
    const calcTotals = () => {
        const data = filteredRows();
        return {
            total_Implements: data.reduce((s, r) => s + (Number(r.total_Implements) || 0), 0),
            working_Count: data.reduce((s, r) => s + (Number(r.working_Count) || 0), 0),
            notWorking_Count: data.reduce((s, r) => s + (Number(r.notWorking_Count) || 0), 0),
        };
    };
//alert(sessionStorage.getItem("stateNameModel"));
    const exportToCsv = () => {
        const totals = calcTotals();
        const header = columns.map(c => c.label).join(',');
        const dataRows = filteredRows().map(r =>
            columns.map(c => `"${String(getValue(r, c.key)).replace(/"/g, '""')}"`).join(',')
        );
        // Total row must follow the same column order as the header
        const totalRow = columns.map(c => {
            if (c.key === 'stateName') return '"Total"';
            if (c.key === 'finYear') return '""';
            return `"${totals[c.key] ?? ''}"`;
        }).join(',');

        const csv = [header, ...dataRows, totalRow].join('\n');
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'crm_chc_updated_data.csv');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };
//alert(sessionStorage.getItem("stateModel"));
    const renderTable = () => {
        const sorted = sortedRows();
        const totalItems = sorted.length;
        const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
        const safePage = Math.min(currentPage, totalPages);
        const pageRows = sorted.slice((safePage - 1) * pageSize, safePage * pageSize);
        const totals = calcTotals();

        return (
            <>
                {/* Filters */}
                <div className="row mb-3">
                    <div className="col-md-3">
                        <label className="form-label">State</label>
                        <select
                            className="form-select"
                            value={selectedState}
                            onChange={e => { setSelectedState(e.target.value); setSelectedDistrict(''); setSelectedSubDistrict(''); setCurrentPage(1); }}
                            disabled={isStateFrozen}
                        >
                            <option value="">All States</option>
                            {statesData.map((item, idx) => (
                                <option key={idx} value={item.stateCode}>
                                    {item.stateName}
                                </option>
                            ))}
                        </select>
                    </div>
                    {!isCentralLogin && (
                        <div className="col-md-3">  
                            <label className="form-label">District</label>
                            <select
                                className="form-select"
                                value={selectedDistrict}
                                onChange={e => { setSelectedDistrict(e.target.value); setSelectedSubDistrict(''); setCurrentPage(1); }}
                                disabled={isDistrictFrozen || !selectedState}
                            >
                                <option value="">All Districts</option>
                                {districtsData.map((item, idx) => (
                                    <option key={idx} value={item.districtCode}>
                                        {item.districtName}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}
                    {!isCentralLogin && (selectedDistrict || isDistrictFrozen) && (
                        <div className="col-md-3">
                            <label className="form-label">Sub District</label>
                            <select
                                className="form-select"
                                value={selectedSubDistrict}
                                onChange={e => { setSelectedSubDistrict(e.target.value); setCurrentPage(1); }}
                            >
                                <option value="">All Sub Districts</option>
                                {subDistrictsData.map((item, idx) => (
                                    <option key={idx} value={item.subDistrictCode}>
                                        {item.subDistrictName}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}
                    <div className="col-md-3">
                        <label className="form-label">Financial Year</label>
                        <select
                            className="form-select"
                            value={selectedFinYear}
                            onChange={e => { setSelectedFinYear(e.target.value); setCurrentPage(1); }}
                        >
                            <option value="">All Years</option>
                            {(allFinYears || []).map((fy, idx) => (
                                <option key={idx} value={fy}>{fy}</option>
                            ))}
                        </select>
                    </div>
                </div>

                {/* Table controls */}
                <div className="table-responsive">
                    <div className="d-flex align-items-center justify-content-between mb-2">
                        <div className="d-flex align-items-center gap-2">
                            <label className="me-2">Page size</label>
                            <select
                                className="form-select form-select-sm"
                                style={{ width: 90 }}
                                value={pageSize}
                                onChange={e => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}
                            >
                                <option value={10}>10</option>
                                <option value={25}>25</option>
                                <option value={50}>50</option>
                                <option value={100}>100</option>
                            </select>
                            <span className="ms-3">Total: {totalItems}</span>
                        </div>
                        <button type="button" className="btn btn-outline-primary btn-sm" onClick={exportToCsv}>
                            Export CSV
                        </button>
                    </div>

                    <table className="table table-bordered table-striped table-sm">
                        <thead>
                            <tr>
                                {columns.map(c => (
                                    <th
                                        key={c.key}
                                        role="button"
                                        onClick={() => handleSort(c.key)}
                                        title="Click to sort"
                                    >
                                        {c.label}
                                        {sortKey === c.key && (
                                            <span className="ms-1">{sortDir === 'asc' ? '▲' : '▼'}</span>
                                        )}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {pageRows.length > 0 ? (
                                <>
                                    {pageRows.map((r, idx) => (
                                        <tr key={idx}>
                                            {columns.map(c => (
                                                <td key={c.key}>{String(getValue(r, c.key))}</td>
                                            ))}
                                        </tr>
                                    ))}
                                    {/* Total row — shown only on last page, columns match header order */}
                                    {safePage === totalPages && (
                                        <tr style={{ fontWeight: 'bold', backgroundColor: '#f8f9fa' }}>
                                            {columns.map(c => {
                                                if (c.key === 'stateName') return <td key={c.key}>Total</td>;
                                                if (c.key === 'finYear') return <td key={c.key}></td>;
                                                return <td key={c.key}>{totals[c.key]}</td>;
                                            })}
                                        </tr>
                                    )}
                                </>
                            ) : (
                                <tr>
                                    <td colSpan={columns.length} className="text-center">
                                        No data available
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>

                    {/* Pagination */}
                    <div className="d-flex align-items-center justify-content-between">
                        <div>Page {safePage} of {totalPages}</div>
                        <div className="btn-group btn-group-sm" role="group">
                            <button type="button" className="btn btn-outline-secondary" onClick={() => goToPage(1)} disabled={safePage === 1}>{'<<'}</button>
                            <button type="button" className="btn btn-outline-secondary" onClick={() => goToPage(safePage - 1)} disabled={safePage === 1}>{'<'}</button>
                            <button type="button" className="btn btn-outline-secondary" onClick={() => goToPage(safePage + 1)} disabled={safePage === totalPages}>{'>'}</button>
                            <button type="button" className="btn btn-outline-secondary" onClick={() => goToPage(totalPages)} disabled={safePage === totalPages}>{'>>'}</button>
                        </div>
                    </div>
                </div>
            </>
        );
    };

    return (
        <>
            <AdminHeaderNew />
            <AdminSideBarNew />

            <main id="main" className="main">
                <div className="pagetitle page_title_box d-flex align-items-center justify-content-between">
                    <nav>
                        <h1>CRM CHC Updated Data</h1>
                        <ol className="breadcrumb">
                            <li className="breadcrumb-item">Utilization</li>
                            <li className="breadcrumb-item active">CRM CHC Updated Data</li>
                        </ol>
                    </nav>
                </div>

                <section className="section">
                    {errorMessage && (
                        <div className="alert alert-danger mt-3" role="alert">{errorMessage}</div>
                    )}

                    <div className="row mt-3">
                        <div className="col-lg-12">
                            <div className="card">
                                <div className="card-body">
                                    <h5 className="card-title">CRM CHC Updated Record</h5>
                                    {renderTable()}
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {loading && (
                    <div className="overlay">
                        <div className="overlay__inner">
                            <div className="overlay__content">
                                <span className="spinner"></span>
                            </div>
                        </div>
                    </div>
                )}
            </main>

            <AdminFooterNew />
        </>
    );
};

export default CRMCHCUpdatedData;
